import { mapGetters, mapState } from 'vuex'

import gaMixin from '@/mixins/ga'
import detectMobileMixin from '@/mixins/detectMobile'
import { isTopSlideshow } from '@/helpers/settings'

import FullStickyBg from '@/components/fullStickyBg/fullStickyBg'
import Icon from '@/components/icon'
import ImgWatermark from '@/components/imgWatermark/imgWatermark'
import loaderImg from '@/assets/svg/lazy-loader.svg'

const ANIMATIONS = [
  'animation-ken-burns-1',
  'animation-ken-burns-2',
  'animation-ken-burns-3',
  'animation-ken-burns-4',
  'animation-ken-burns-5',
  'animation-ken-burns-6',
  'animation-ken-burns-7',
  'animation-ken-burns-8',
  'animation-ken-burns-9',
  'animation-ken-burns-10'
]

export default {
  mixins: [gaMixin, detectMobileMixin],
  components: {
    appFullStickyBg: FullStickyBg,
    appIcon: Icon,
    appImgWatermark: ImgWatermark
  },
  props: {
    topSection: {
      default: false,
      type: Boolean
    }
  },
  data () {
    return {
      slideshow: null,
      start: false,
      indexSlide: 0,
      classActive: 'slideshow__item--active',
      timeout: null,
      isPlaying: false,
      isFullscreen: false,
      loaderImg: loaderImg,
      isSafari: false,
      psOptions: {
        wheelSpeed: 2,
        wheelPropagation: false,
        minScrollbarLength: 20,
        suppressScrollY: true
      }
    }
  },
  computed: {
    ...mapState('tours', ['photos', 'ready', 'settings']),
    ...mapState('utils', ['widgets']),
    ...mapGetters('tours', ['getUnpaid', 'getPhotos']),
    photosCount () {
      return this.photos.length
    },
    getIndexSlide () {
      return this.indexSlide
    },
    animationsIsOn () {
      if (!this.settings.slideshowConfig) {
        return false
      }

      return this.settings.slideshowConfig.animations
    },
    thumbnailsIsOn () {
      if (!this.settings.slideshowConfig) {
        return false
      }

      return this.settings.slideshowConfig.thumbnails
    },
    autoplayIsOn () {
      if (!this.settings.slideshowConfig) {
        return false
      }

      return this.settings.slideshowConfig.autoplay && !this.getUnpaid
    },
    isTopSlideshow () {
			return isTopSlideshow(this.settings)
		},
    fitToScreenIsOn () {
      return this.settings.slideshowConfig.fitToScreen;
    },
    duration () {
      return this.settings.slideshowConfig && this.settings.slideshowConfig.duration ? this.settings.slideshowConfig.duration : 5000;
    }
  },
  methods: {
    isVertical (event) {
      const el = event.target;

      if (el.naturalHeight > el.naturalWidth) {
        el.classList.add('slideshow__item-img--vertical');
      }
    },
    getPhotoBySize (photo) {
      if (this.isMobile()) {
        return photo.md
      }

      return photo.xl
    },
    getPhotoThumbnail (photo) {
      if (photo.urls[3]) {
        return photo.urls[3].url
      }

      return photo.urls[2].url
    },
    getKenBurnsAnimation () {
      return ANIMATIONS[Math.floor(Math.random() * ANIMATIONS.length)]
    },
    getActiveAnimationClassName (index) {
      return [...this.$refs.slide[index].classList].find(item => item.match('animation-ken-burns'))
    },
    loadSlides (index) {
      const numberOfNextSlides = 3
      const numberOfSlides = this.photos.length
      const indexes = [...Array(numberOfSlides).keys()]

      // get active element and previous {numberOfNextSlides} elements on left side
      let slidesOnTheLeft = []

      if (index >= numberOfNextSlides) {
        slidesOnTheLeft = indexes.slice(index - numberOfNextSlides, index + 1)
      } else {
        slidesOnTheLeft = [...indexes.slice(0, index + 1), ...indexes.slice(index - numberOfNextSlides)]
      }

      // get active element and next {numberOfNextSlides} elements on right side
      const slidesOnTheRight = indexes.slice(index, index + numberOfNextSlides + 1)

      let slidesToBeLoaded = slidesOnTheLeft.concat(slidesOnTheRight)
      slidesToBeLoaded = [...new Set([...slidesOnTheLeft,...slidesOnTheRight])]

      slidesToBeLoaded.forEach(value => {
        this.$refs.slide[value].style.display = 'block'

        if (this.animationsIsOn && !this.getActiveAnimationClassName(value)) {
          this.$refs.slide[value].classList.add(this.getKenBurnsAnimation())
        }
      })
      
      indexes.forEach(value => {
        if (!slidesToBeLoaded.includes(value)) {
          if (this.animationsIsOn) {
            const animationClassName = this.getActiveAnimationClassName(value)

            if (animationClassName) {
              this.$refs.slide[value].classList.remove(animationClassName)
            }
          }
          
          this.$refs.slide[value].style.display = 'none'
        }
      })
    },
    addActiveSlide (index) {
      const el = this.$refs.slide[index].childNodes[0]

      this.loadSlides(index)

      el.style.animation = '2'
      el.offsetHeight; /* trigger reflow */
      this.$refs.slide[index].classList.add(this.classActive)
      el.style.animation = null
    },
    removeActiveSlide () {
      this.$refs.slide.forEach((item) => {
        item.classList.remove(this.classActive)
      })
    },
    prevSlide () {
      if (this.indexSlide === 0) {
        this.indexSlide = this.$refs.slide.length - 1
      } else {
        this.indexSlide = --this.indexSlide
      }

      this.removeActiveSlide()
      this.addActiveSlide(this.indexSlide)
    },
    nextSlide () {
      if (this.indexSlide === this.$refs.slide.length - 1) {
        this.indexSlide = 0
      } else {
        this.indexSlide = ++this.indexSlide
      }

      this.removeActiveSlide()
      this.addActiveSlide(this.indexSlide)
    },
    runTimeout () {
      this.timeout = setTimeout(() => {
        this.nextSlide()
        this.runTimeout()
      }, this.duration)
    },
    pauseTimeout () {
      clearTimeout(this.timeout)
    },
    resetTimeout () {
      if (this.isPlaying) {
        this.pauseTimeout()
        this.runTimeout()
      }
    },
    togglePlay () {
      this.isPlaying = !this.isPlaying

      if (this.isPlaying) {
        this.runTimeout()
      } else {
        this.pauseTimeout()
      }
    },
    requestFullscreen () {
      const el = this.slideshow
      
      if (el.requestFullscreen) {
				el.requestFullscreen()
			} else if (el.mozRequestFullScreen) {
				el.mozRequestFullScreen() // Firefox
			} else if (el.webkitRequestFullscreen) {
				el.webkitRequestFullscreen() // Chrome and Safari
      }
    },
    exitFullscreen () {
      if (document.exitFullscreen) {
				document.exitFullscreen()
			} else if (document.mozExitFullscreen) {
				document.mozExitFullscreen() // Firefox
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen() // Chrome and Safari
      }
    },
    changeThumbnailItem () {
      if (!this.thumbnailsIsOn) {
        return
      }

      const el = this.$refs.thumbnailItem[this.indexSlide]
      const rect = el.getBoundingClientRect()
      const container = this.$refs.ps.$el.getBoundingClientRect()

      if (rect.left < container.left || rect.right > container.width) {
        this.$refs.ps.$el.scroll({
          top: 0,
          left: el.offsetLeft + (rect.width / 2) - (container.width / 2),
          behavior: 'smooth'
        });
      }
    },
    onClickNextSlide () {
      this.resetTimeout()
      this.nextSlide()
    },
    onClickPrevSlide () {
      this.resetTimeout()
      this.prevSlide()
    },
    onClickThumbnail (index) {
      this.resetTimeout()
      
      this.indexSlide = index
      this.removeActiveSlide()
      this.addActiveSlide(this.indexSlide)
    },
    onArrowsKeyUp (evt) {
      switch (evt.code) {
        case 'ArrowLeft':
          this.onClickPrevSlide()
          break
        case 'ArrowRight':
          this.onClickNextSlide()
          break
        case 'Space':
          this.togglePlay()
          break
      }
    },
    onFullscreen () {
      if (this.isFullscreen) {
        this.exitFullscreen()
      } else {
        this.requestFullscreen()
      }
    },
    onFullscreenState () {
      const el = document

			if(el.fullscreenElement ||
				el.mozFullScreenElement || 
				el.msFullscreenElement ||
				el.webkitFullscreenElement
			) {
        this.isFullscreen = true

        document.addEventListener('keyup', this.onArrowsKeyUp)
			} else {
        this.isFullscreen = false

        document.removeEventListener('keyup', this.onArrowsKeyUp)
			}
    },
    onStart () {
      this.start = true

      this.togglePlay()

      this.$emit('onPlay')
      this.sendEventGA('Slideshow', 'Play')
    },
    isSafariBrowser () {
      if (navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1) {
        this.isSafari = true
      }
    },
    init () {
      if (this.ready) {
        this.slideshow = this.$refs.slideshow
        this.addActiveSlide(this.indexSlide)

        this.slideshow.addEventListener('fullscreenchange', this.onFullscreenState)

        if (this.isPlaying) {
          this.runTimeout()
        }

        this.isSafariBrowser()

        if (this.autoplayIsOn) {
          this.onStart()
        }
      }
    }
  },
  mounted() {
    this.init()
  },
  destroyed () {
    this.pauseTimeout()

    this.slideshow.removeEventListener('fullscreenchange', this.onFullscreenState)
  },
  watch: {
    ready () {
      this.init()
    },
    getIndexSlide () {
      this.changeThumbnailItem()
    }
  }
}
